///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useState, useRef, useEffect } from "react";

import { useAuth } from "../../context/AuthContext";
import { getOneMonthDif, getMonthName } from "../../services/DateService";
import { VERSION as DATA_YEAR } from "../../databases/CodVamal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSpinner,
  faArrowRight,
  faTableCells,
  faTableColumns,
  faMagnifyingGlass,
  faCheck,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import { createNotification } from "../../services/NotificationService";
import HandleExcel from "./HandleExcel";
import {
  getExcelSheets,
  getHeaders,
  getSheetValues,
} from "../../services/ExcelService";

const ImportExcel = () => {
  const FINAL_STEP = 4;
  const SLEEP_TIME = 1000;

  //? Above this many entries the picker gets a search box. Below it, scanning the
  //? cards by eye is faster than typing.
  const SEARCH_FROM = 4;

  const today = getOneMonthDif(-1);

  const [loading, setLoading] = useState(false);
  const [handle, setHandle] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState(0);
  const [percent, setPercent] = useState(0);
  const [data, setData] = useState({
    file: "",
    sheets: "",
    headers: "",
    layout: 0,
    //? The declaration year is not a free choice: a declaration must use the CN
    //? nomenclature of its reference period, and the only nomenclature shipped with the
    //? app is CodVamal's VERSION. Any other year would produce codes we cannot validate.
    year: DATA_YEAR,
    month: today.month,
    tert: false,
    compFor: 0,
    valStat: false,
  });
  const [generating, setGenerating] = useState(false);
  const [fileSheets, setFileSheets] = useState([]);
  const [sheetQuery, setSheetQuery] = useState("");
  const [layoutQuery, setLayoutQuery] = useState("");
  const [suggestedLayout, setSuggestedLayout] = useState(-1);

  const inputFile = useRef();

  const { layouts, userData, companies } = useAuth();

  useEffect(() => {
    step === FINAL_STEP && setDone(true);
    setPercent(step > 0 ? (step * 100) / FINAL_STEP : 0);
  }, [step]);

  useEffect(() => {
    done && generatingFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  //-----------------------------------------------------------------------------------
  //* \brief
  //? Row / column count of a sheet, used on the sheet cards so the user can tell the
  //? data sheet from the empty ones without opening anything.
  //-----------------------------------------------------------------------------------
  const sheetStats = (index) => {
    const rows = data.sheetValues && data.sheetValues[index];
    if (!Array.isArray(rows) || rows.length === 0) return { rows: 0, cols: 0 };
    const cols = Object.keys(rows[0]).filter((k) => k !== "__rowNum__").length;
    return { rows: rows.length, cols };
  };

  const matchesQuery = (text, query) =>
    query.trim() === "" ||
    text.toString().toLowerCase().includes(query.trim().toLowerCase());

  const companyNameFor = (cif) => {
    const found = companies.find((c) => c.cif === cif);
    return found ? found.name : "";
  };

  const checkValues = () => {
    if (data.file === "") {
      createNotification("error", `Fișierul este invalid`);
      return false;
    }
    if (parseInt(data.year) !== DATA_YEAR) {
      createNotification(
        "error",
        `Anul declarației trebuie să fie ${DATA_YEAR} - singurul an pentru care este disponibil Nomenclatorul Combinat`
      );
      return false;
    }
    if (parseInt(data.month) < 1 || data.month > 12) {
      createNotification("error", `Luna este invalidă`);
      return false;
    }
    return true;
  };

  const generatingFunc = async () => {
    setGenerating(true);
    createNotification("information", `Se generează`);
    const error = !checkValues();
    if (error) {
      setDone(false);
      setStep(0);
      setGenerating(false);
      return false;
    }
    await new Promise((r) => setTimeout(r, SLEEP_TIME));
    setHandle(true);
  };

  const handleExcelUpload = async (files) => {
    if (files.length < 1) {
      return createNotification("error", `Fișierul este invalid`);
    }
    let file = files[0];
    if (
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" &&
      file.type !== "application/vnd.ms-excel"
    ) {
      return createNotification("error", `Fișierul este invalid`);
    }
    setData((prev) => ({ ...prev, file: file }));

    setLoading(true);
    const sheets = getExcelSheets(file);
    await new Promise((r) => setTimeout(r, 200));
    if (sheets.length < 1) {
      setLoading(false);
      return createNotification("error", `Fișierul nu conține date.`);
    }
    const sheetValues = getSheetValues(file);
    await new Promise((r) => setTimeout(r, 200));
    setFileSheets(sheets);
    setData((prev) => ({ ...prev, sheets: sheets, sheetValues: sheetValues }));
    await new Promise((r) => setTimeout(r, 200));
    setLoading(false);
  };

  const finishExcelUpload = async (headerIdx = 0) => {
    setLoading(true);
    const headers = getHeaders(data.sheetValues[headerIdx]);
    setData((prev) => ({
      ...prev,
      headers: headers,
      sheetValues: prev.sheetValues[headerIdx],
    }));
    await new Promise((r) => setTimeout(r, 200));
    setLoading(false);
    createNotification("success", `Fișierul a fost importat`);
    const nextStep = step + 1;
    setStep(nextStep);
  };

  const handleUpload = () => {
    inputFile.current.click();
  };

  const handleSteps = (dir) => {
    const nextStep = step + dir;
    if (nextStep === 0) {
      // Going back to beginning resets imported file
      setFileSheets([]);
    }
    setStep(nextStep);
  };

  const autoSetFromLayout = () => {
    // return if user is not on tert account
    if (!userData.data.tert) return false;
    const idx = parseInt(data.layout);
    // check if the layout is for users company or else set tert
    if (layouts[idx].company === companies[0].cif) {
      setData((prev) => ({ ...prev, tert: false }));
    } else {
      setData((prev) => ({ ...prev, tert: true }));
      // check if company still exists still
      let toReturn = -1;
      toReturn = companies.map((c) => c.cif).indexOf(layouts[idx].company);
      if (toReturn !== -1) setData((prev) => ({ ...prev, compFor: toReturn }));
    }
  };

  //-----------------------------------------------------------------------------------
  //* \brief
  //? Preselects the layout whose name matches the imported file name, and remembers
  //? which one it was so the card can be marked as a suggestion.
  //?
  //? The previous version split an already upper-cased name on /[^a-z]/, which matches
  //? every character, so the word list was all empty strings and indexOf('') returned 0
  //? for every layout - the last layout in the list always won. It also used `return`
  //? inside forEach, which skips an iteration instead of stopping. Both are fixed here,
  //? otherwise the "suggested" badge would be pointing at an arbitrary layout.
  //-----------------------------------------------------------------------------------
  const autoSetLayout = () => {
    const fileName = data.file !== "" ? data.file.name.toString().toUpperCase() : "";
    if (fileName === "") return;

    const layoutName = (i) => layouts[i].name.toString().toUpperCase().trim();
    let found = -1;

    // 1. one name contains the other
    for (let i = 0; i < layouts.length; i++) {
      const name = layoutName(i);
      if (name === "") continue;
      if (fileName.includes(name) || name.includes(fileName)) {
        found = i;
        break;
      }
    }
    // 2. otherwise a meaningful word of the file name appears in the layout name
    if (found === -1) {
      const words = fileName.split(/[^A-Z0-9]+/).filter((w) => w.length > 2);
      for (let i = 0; i < layouts.length; i++) {
        const name = layoutName(i);
        if (name === "") continue;
        if (words.some((word) => name.includes(word))) {
          found = i;
          break;
        }
      }
    }

    setSuggestedLayout(found);
    if (found !== -1) setData((prev) => ({ ...prev, layout: found }));
  };

  // Download the example excel file
  const handleDownloadExcel = () => {
    const fileUrl = "/files/Exemplu_achizitii_programintrastat.xlsx";
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "Exemplu_achizitii_programintrastat.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    handleUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    autoSetFromLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.layout]);

  useEffect(() => {
    autoSetLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.file]);

  return (
    <div className="import-wrapper">
      {!handle && (
        <div className="import-section">
          <div className="step-indicator">
            {step !== FINAL_STEP ? (
              <>Pasul <b>{step + 1}</b> / {FINAL_STEP}</>
            ) : (
              <b>Finalizat</b>
            )}
            {percent > 0 && <span className="step-percent">{percent}%</span>}
          </div>
          <div className="width-wrapper">
            <div className="progress-bar-wrapper">
              <div className="progress-bar" style={{ width: `${percent}%` }}></div>
            </div>
          </div>

          {step !== 0 && step !== FINAL_STEP && (
            <div className="small-form-wrapper">
              <div className="steps-buttons">
                <div
                  className="button-secondary"
                  onClick={() => handleSteps(-1)}
                >
                  Pas anterior
                </div>
                <div
                  className="button-secondary selected-btn"
                  onClick={() => handleSteps(1)}
                >
                  {step + 1 !== FINAL_STEP ? "Pas următor" : "ÎNCARCĂ"}
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </div>
            </div>
          )}

          {step === 0 && (
            <div className="upload-section">
              <h2>Importă fișierul Excel</h2>
              {loading ? (
                <FontAwesomeIcon className="spinner" icon={faSpinner} />
              ) : !fileSheets.length > 0 ? (
                <>
                  <div className="upload-here" onClick={() => handleUpload()}>
                    <FontAwesomeIcon className="upload-icon" icon={faPlus} />
                    <input
                      type="file"
                      id="file"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      onChange={(e) => handleExcelUpload(e.target.files)}
                      ref={inputFile}
                      style={{ display: "none" }}
                    />
                  </div>
                  <p>
                    <a
                      href="https://landing.programintrastat.ro/ce-declar-la-intrastat"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ce trebuie să conțină fișierul Excel importat?
                    </a>
                  </p>
                  <p>
                    <span className="link-style" onClick={handleDownloadExcel}>
                      Descarcă exemplu de fișier Excel
                    </span>
                  </p>
                  <p>
                    <a href="https://programintrastat.ro/docs">
                      Instrucțiuni pas cu pas
                    </a>
                  </p>
                </>
              ) : (
                <div className="picker">
                  <div className="picker__head">
                    <span className="picker__title">Alege fișa de lucru</span>
                    <span className="picker__count">
                      {fileSheets.length}{" "}
                      {fileSheets.length === 1 ? "fișă" : "fișe"}
                    </span>
                  </div>

                  {fileSheets.length > SEARCH_FROM && (
                    <div className="picker__search">
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                      <input
                        type="text"
                        value={sheetQuery}
                        placeholder="Caută după nume..."
                        onChange={(e) => setSheetQuery(e.target.value)}
                        autoFocus
                      />
                    </div>
                  )}

                  <div className="picker__grid">
                    {fileSheets.map((name, index) => {
                      if (!matchesQuery(name, sheetQuery)) return null;
                      const { rows, cols } = sheetStats(index);
                      const isEmpty = rows === 0;
                      return (
                        <button
                          type="button"
                          key={`${name} - ${index}`}
                          className={`pick-card${isEmpty ? " pick-card--empty" : ""}`}
                          disabled={isEmpty}
                          title={isEmpty ? `${name} - nu conține date` : name}
                          onClick={() => finishExcelUpload(index)}
                        >
                          <span className="pick-card__icon">
                            <FontAwesomeIcon icon={isEmpty ? faBan : faTableCells} />
                          </span>
                          <span className="pick-card__body">
                            <span className="pick-card__name">{name}</span>
                            <span className="pick-card__meta">
                              {isEmpty
                                ? "Fără date"
                                : `${rows.toLocaleString("ro-RO")} ${
                                    rows === 1 ? "rând" : "rânduri"
                                  } · ${cols} ${cols === 1 ? "coloană" : "coloane"}`}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {fileSheets.every((name) => !matchesQuery(name, sheetQuery)) && (
                    <p className="picker__empty">
                      Nicio fișă de lucru nu conține „{sheetQuery}”.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="small-form-wrapper small-form-wrapper--wide">
              <h2>Macheta și perioada</h2>

              <div className="picker">
                <div className="picker__head">
                  <span className="picker__title">Machetă</span>
                  <span className="picker__count">
                    {layouts.length}{" "}
                    {layouts.length === 1 ? "machetă" : "machete"}
                  </span>
                </div>

                {layouts.length > SEARCH_FROM && (
                  <div className="picker__search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input
                      type="text"
                      value={layoutQuery}
                      placeholder="Caută machetă după nume sau firmă..."
                      onChange={(e) => setLayoutQuery(e.target.value)}
                    />
                  </div>
                )}

                <div className="picker__grid">
                  {layouts.map((lay, index) => {
                    const owner = companyNameFor(lay.company);
                    if (
                      !matchesQuery(lay.name, layoutQuery) &&
                      !matchesQuery(owner, layoutQuery)
                    )
                      return null;

                    const selected = parseInt(data.layout) === index;
                    const isArrival = lay.type === "achizitii";
                    return (
                      <button
                        type="button"
                        key={`${lay.dataId} - ${lay.name}`}
                        className={`pick-card${selected ? " pick-card--on" : ""}`}
                        title={lay.name}
                        onClick={() =>
                          setData((prev) => ({ ...prev, layout: index }))
                        }
                      >
                        <span className="pick-card__icon">
                          <FontAwesomeIcon
                            icon={selected ? faCheck : faTableColumns}
                          />
                        </span>
                        <span className="pick-card__body">
                          <span className="pick-card__name">{lay.name}</span>
                          <span className="pick-card__meta">
                            <span
                              className={`pick-card__tag pick-card__tag--${
                                isArrival ? "arrival" : "dispatch"
                              }`}
                            >
                              {isArrival ? "Achiziții" : "Livrări"}
                            </span>
                            {owner !== "" && (
                              <span className="pick-card__owner">{owner}</span>
                            )}
                          </span>
                        </span>
                        {suggestedLayout === index && (
                          <span
                            className="pick-card__hint"
                            title="Potrivită după numele fișierului importat"
                          >
                            sugerată
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {layouts.length === 0 ? (
                  <p className="picker__empty">
                    Nu aveți nicio machetă. Creați una din secțiunea „Machete”
                    înainte de a importa un fișier.
                  </p>
                ) : (
                  layouts.every(
                    (lay) =>
                      !matchesQuery(lay.name, layoutQuery) &&
                      !matchesQuery(companyNameFor(lay.company), layoutQuery)
                  ) && (
                    <p className="picker__empty">
                      Nicio machetă nu corespunde căutării „{layoutQuery}”.
                    </p>
                  )
                )}
              </div>

              <div className="period">
                <div className="period__block">
                  <span className="picker__title">Lună</span>
                  <div className="month-grid">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <button
                        type="button"
                        key={`month-${m}`}
                        className={`month-chip${
                          parseInt(data.month) === m ? " month-chip--on" : ""
                        }`}
                        title={getMonthName(m)}
                        onClick={() =>
                          setData((prev) => ({ ...prev, month: m }))
                        }
                      >
                        {getMonthName(m).slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="period__block period__block--year">
                  <span className="picker__title">An</span>
                  <div className="locked-field" title={`Anul declarației este ${DATA_YEAR}`}>
                    <span className="locked-field__value">{DATA_YEAR}</span>
                    {/* <span className="locked-field__note">
                      <FontAwesomeIcon icon={faCircleInfo} /> Singurul an
                      disponibil: declarația foloseşte Nomenclatorul Combinat{" "}
                      {DATA_YEAR}
                    </span> */}
                  </div>
                </div>
              </div>

              <p className="period__selected">
                Se declară perioada{" "}
                <b>
                  {getMonthName(parseInt(data.month))} {DATA_YEAR}
                </b>
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="small-form-wrapper">
              <h2>Firme</h2>
              <div className="form-section-label small-form-section">
                <p>{data.tert ? "Firmă terță" : "Firma mea"}</p>
                <input
                  disabled={true}
                  type="text"
                  className="input-text"
                  id="comp"
                  value={companies[0].name}
                />
                {userData.data.tert && (
                  <>
                    <div
                      className="checkbox-wrapper"
                      style={{ marginTop: "2em" }}
                    >
                      <input
                        className="checkbox"
                        value={data.tert}
                        defaultChecked={data.tert}
                        onChange={(e) =>
                          setData((prev) => ({
                            ...prev,
                            tert: e.target.checked,
                          }))
                        }
                        type="checkbox"
                        id="tert"
                        name="tert"
                        title="Declarant terț"
                      />
                      <label htmlFor="tert">Declarație terță</label>
                    </div>

                    {data.tert && (
                      <>
                        <p>Pentru firma</p>
                        <select
                          name="compT"
                          value={data.compFor}
                          onChange={(e) =>
                            setData((prev) => ({
                              ...prev,
                              compFor: e.target.value,
                            }))
                          }
                          id="compT"
                          className="input-text"
                        >
                          {companies.length > 1 &&
                            companies.map((comp, index) => {
                              if (index > 0)
                                return (
                                  <option
                                    key={`${comp.dataId} - ${comp.name}`}
                                    value={index}
                                  >
                                    {comp.name}
                                  </option>
                                );
                              else return null;
                            })}
                        </select>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="small-form-wrapper">
              <h2>Rezumat</h2>
              <div className="form-section-label small-form-section">
                <p>Fișier</p>
                <input
                  disabled={true}
                  type="text"
                  className="input-text"
                  id="fNameR"
                  value={data.file !== "" ? data.file.name : ""}
                />

                <p>Machetă</p>
                <input
                  disabled={true}
                  type="text"
                  className="input-text"
                  id="layoutR"
                  value={layouts[data.layout].name}
                />

                <p>Perioadă</p>
                <input
                  disabled={true}
                  type="text"
                  className="input-text"
                  id="periodR"
                  value={`${getMonthName(parseInt(data.month))} ${DATA_YEAR}`}
                />

                <p>Firma mea</p>
                <input
                  disabled={true}
                  type="text"
                  className="input-text"
                  id="compR"
                  value={companies[0].name}
                />

                {data.tert && (
                  <>
                    <p>Pentru firma</p>
                    <input
                      disabled={true}
                      type="text"
                      className="input-text"
                      id="compForR"
                      value={companies[data.compFor].name}
                    />
                  </>
                )}

                {/* <div className='checkbox-wrapper' style={{marginTop:'2em'}}>
                            <input className='checkbox' value={data.valStat} defaultChecked={data.valStat}
                                onChange={(e) => setData(prev => ({...prev, valStat: e.target.checked}))}
                                type="checkbox" id="valStat" name="valStat" title='Calcul valoare statistică'/>
                            <label htmlFor="valStat">Calcul valoare statistică
                            </label>
                        </div> */}
              </div>
            </div>
          )}

          {generating && (
            <>
              <FontAwesomeIcon className="generating-spin" icon={faSpinner} />
              <h2>Se generează..</h2>
            </>
          )}
        </div>
      )}
      {handle && <HandleExcel startData={data} />}
    </div>
  );
};

export default ImportExcel;
